import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { toggleControl, toggleDialog } from '../../../store/control/duck';
import ContentsDialog, { getLocalePikto } from '../ContentsDialog';
import Pikto from '../../Pikto/Pikto';
import _ from 'lodash';
import { shouldEnableRecruitmentMode } from '../../../utils/user';
import MDRTActivityCalculator from './MDRTActivityCalculator/MDRTActivityCalculator';
import { MDRT_CALCULATOR_DEFAULT_STATES } from '../../../constants/mdrt-calculator';
import { RECRUITMENT_INCOME_SIM_ITEM_ID, RECRUITMENT_MDRT_ITEM_ID } from '../../../constants/recruitment';
import IncomeSimulator from './IncomeSimulator/IncomeSimulator';

const CONTENTS = [
  {
    id: 'HKMkt-adv',
    title: '香港市場優勢 (示範)',
    type: 'pikto',
    section: 'recruit-charts-sub-1',
    color: '#5fc3d2',
    render: ({ intl }) => <Pikto uid="57748719-career-market-advantage" />
  },
  {
    id: 'support-industry',
    title: '政策支持理財行業 (示範)',
    type: 'pikto',
    section: 'recruit-charts-sub-1',
    color: '#5fc3d2',
    render: ({ intl }) => <Pikto uid="57751074-career-government-help" />
  },
  {
    id: 'MDRT-into',
    title: 'MDRT介紹 (示範)',
    type: 'pikto',
    section: 'recruit-charts-sub-1',
    color: '#5fc3d2',
    render: ({ intl }) => <Pikto uid="57749055-career-mdrt-intro" />
  },
  {
    id: 'Adviser-6-advantage',
    title: '理財行業的優勢 (示範)',
    type: 'pikto',
    section: 'recruit-charts-sub-1',
    color: '#5fc3d2',
    render: ({ intl }) => <Pikto uid="58779297-adviser-6-advantage" />
  },
  {
    id: 'MDRT-quarlities',
    title: '專業顧問的特質 (示範)',
    type: 'pikto',
    section: 'recruit-charts-sub-1',
    color: '#5fc3d2',
    render: ({ intl }) => <Pikto uid="58981584-mdrt-quarlities-recruitment" />
  }
];

const DEFAULT_ITEM = CONTENTS.find(item => item.id === 'HKMkt-adv');

/**
 * Hoisted out of the sections below so a caller can be landed straight on it.
 * ClientV2's 招募 view has its own 活動量 tile, and a tile that opens a library
 * the adviser then has to search is not the tile they tapped.
 */
const MDRT_CALCULATOR_ITEM = {
  id: RECRUITMENT_MDRT_ITEM_ID,
  title: ({ intl }) => intl.formatMessage({ id: 'recruitment-dialog-tools-mdrt-cal' }),
  type: 'custom',
  section: 'recruit-tools',
  color: '#3966f8',
  fullScreenDialog: true,
  render: ({ intl }) => <MDRTActivityCalculator />
};

/** 建團隊收入模擬器 — beside the MDRT calculator, and reachable the same way. */
const INCOME_SIMULATOR_ITEM = {
  id: RECRUITMENT_INCOME_SIM_ITEM_ID,
  title: ({ intl }) => intl.formatMessage({ id: 'recruitment-dialog-tools-income-sim' }),
  type: 'custom',
  section: 'recruit-tools',
  color: '#3966f8',
  fullScreenDialog: true,
  render: () => <IncomeSimulator />
};

const RecruitmentContentsDialog = props => {
  const { intl, history, recruitmentDialog, recruitmentContents, recruitmentMode, toggleDialog } = props;

  const { closed, focusItem } = recruitmentDialog || {};
  const open = !!recruitmentDialog && !closed;

  /**
   * The calculator when a caller asked for it, nothing when nobody did.
   *
   * Passed as ContentsDialog's `focusItem` rather than folded into
   * `defaultItem`, because the two mean different things to a phone: a REQUEST
   * is honoured at every width, a DEFAULT only fills the desktop's side pane.
   * Folding them together made a plain 行業材料 open on a phone land on the
   * front chart stacked over the library, which the adviser then had to dismiss
   * to reach the list they had tapped.
   *
   * Plainly, as either the constant or nothing. It was a memoised COPY, so that
   * its identity changed on every open and ContentsDialog's target followed it;
   * the dialog now re-targets on its own opening edge instead, so there is
   * nothing here to signal and nothing to copy.
   */
  const focusedItem =
    focusItem === RECRUITMENT_MDRT_ITEM_ID
      ? MDRT_CALCULATOR_ITEM
      : focusItem === RECRUITMENT_INCOME_SIM_ITEM_ID && recruitmentMode
      ? INCOME_SIMULATOR_ITEM
      : undefined;

  const close = () => {
    // Not `{ ...recruitmentDialog }`. Tidying rather than a fix: every caller
    // that reopens this dialog sends its own payload, so a stale `focusItem`
    // has never reached an open it did not belong to — it simply sat in the
    // store describing a request the closing dialog could no longer act on.
    // `closed` is the whole of what a closing dialog has to carry.
    toggleDialog('recruitment', { closed: true });
  };

  const onClosed = () => {
    toggleDialog('recruitment', false);
    toggleControl('mdrtActivityCalculator', MDRT_CALCULATOR_DEFAULT_STATES);
  };

  return (
    <ContentsDialog
      history={history}
      className="recruitment-dialog"
      open={open}
      onClose={close}
      onClosed={onClosed}
      title={intl.formatMessage({ id: 'recruitment-dialog-title' })}
      type="recruitment"
      collapsibleMenu
      sections={[
        ...(recruitmentContents || []).map(recruitmentContent => {
          const nameLocale = _.get(recruitmentContent.name, intl.locale, '');
          return {
            title: nameLocale,
            img: <img src={recruitmentContent.iconImg} alt={nameLocale} width="42" />,
            items: (recruitmentContent.items || []).map(item => {
              const piktoChartIdLocale = _.get(item.piktoChartIds, intl.locale);
              if (piktoChartIdLocale) {
                const titleLocale = _.get(item.title, intl.locale);
                return {
                  id: piktoChartIdLocale,
                  title: titleLocale,
                  type: 'pikto',
                  color: item.color,
                  render: () => <Pikto uid={piktoChartIdLocale} />
                };
              } else {
                return null;
              }
            }),
            special: true
          };
        }),
        {
          title: intl.formatMessage({ id: 'recruitment-dialog-tools-title' }),
          // The income simulator belongs to 招募模式, so it needs FEATURE_RECRUITMODE.
          items: recruitmentMode ? [MDRT_CALCULATOR_ITEM, INCOME_SIMULATOR_ITEM] : [MDRT_CALCULATOR_ITEM]
        },
        {
          title: intl.formatMessage({ id: 'recruit-charts-sub-1' }),
          items: CONTENTS
        }
      ]}
      defaultItem={DEFAULT_ITEM}
      focusItem={focusedItem}
    />
  );
};

const container = connect(
  state => ({
    recruitmentDialog: state.control.recruitmentDialog,
    recruitmentContents: state.functions.recruitmentContents,
    recruitmentMode: shouldEnableRecruitmentMode({
      user: _.get(state, 'user.userDetails'),
      teamCampaignCodes: _.get(state, 'functions.campaignCodes', [])
    })
  }),
  {
    toggleDialog,
    toggleControl
  }
)(RecruitmentContentsDialog);

export default injectIntl(container);
